<template>
  <div class="ev-year-calendar">
    <div class="ev-year-calendar-header">
      <h4 class="ev-year-calendar-title">
        📅 Pregled odsustava za {{ year }}. godinu
      </h4>
      <div class="ev-year-calendar-legend">
        <span class="ev-yc-legend-item">
          <span class="ev-yc-swatch ev-yc-swatch--past-go"></span>
          Realizovani godišnji odmori
        </span>
        <span class="ev-yc-legend-item">
          <span class="ev-yc-swatch ev-yc-swatch--past-other"></span>
          Realizovana ostala odsustva
        </span>
        <span class="ev-yc-legend-item">
          <span class="ev-yc-swatch ev-yc-swatch--future"></span>
          Planirana odsustva
        </span>
      </div>
    </div>

    <div class="ev-year-calendar-grid">
      <div v-for="month in months" :key="month.index" class="ev-month-card">
        <div class="ev-month-name">{{ month.name }}</div>
        <div class="ev-month-weekdays">
          <span v-for="(wd, idx) in weekdayLabels" :key="idx">{{ wd }}</span>
        </div>
        <div class="ev-month-days">
          <span
            v-for="(cell, idx) in month.cells"
            :key="idx"
            class="ev-day-cell"
            :class="cellClass(cell)"
            :title="cell.title"
          >
            <span v-if="cell.day">{{ cell.day }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const MONTH_NAMES = [
  "Januar",
  "Februar",
  "Mart",
  "April",
  "Maj",
  "Jun",
  "Jul",
  "Avgust",
  "Septembar",
  "Oktobar",
  "Novembar",
  "Decembar",
];

const WEEKDAY_LABELS = ["P", "U", "S", "Č", "P", "S", "N"];

const STATUS_CODE_MAP = {
  2: "GO",
  3: "BO",
  4: "SO",
  5: "PO",
  6: "PR",
  7: "NO",
  8: "SN",
  9: "SR",
  10: "TB",
  12: "NP",
  14: "RK",
};

export default {
  name: "EmployeeYearCalendar",

  props: {
    year: {
      type: Number,
      required: true,
    },
    realizedAbsences: {
      type: Array,
      default: () => [],
    },
    plannedAbsences: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      weekdayLabels: WEEKDAY_LABELS,
    };
  },

  computed: {
    today() {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      return t;
    },

    realizedDayMap() {
      // key: "YYYY-MM-DD" -> { code, statusId }
      const map = {};
      (this.realizedAbsences || []).forEach((abs) => {
        const start = new Date(abs.startDate);
        const end = new Date(abs.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
          if (d.getFullYear() !== this.year) continue;
          const key = this.dateKey(d);
          map[key] = {
            statusId: abs.statusId,
            code: STATUS_CODE_MAP[abs.statusId] || "??",
          };
        }
      });
      return map;
    },

    plannedDayMap() {
      const map = {};
      (this.plannedAbsences || []).forEach((abs) => {
        const start = new Date(abs.startDate);
        const end = new Date(abs.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
          if (d.getFullYear() !== this.year) continue;
          const key = this.dateKey(d);
          map[key] = {
            statusId: abs.statusId,
            code: STATUS_CODE_MAP[abs.statusId] || "??",
          };
        }
      });
      return map;
    },

    months() {
      const result = [];
      for (let m = 0; m < 12; m++) {
        result.push(this.buildMonth(m));
      }
      return result;
    },
  },

  methods: {
    dateKey(d) {
      const y = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${mo}-${day}`;
    },

    buildMonth(monthIndex) {
      const firstDay = new Date(this.year, monthIndex, 1);
      const lastDay = new Date(this.year, monthIndex + 1, 0);

      // Monday-first offset (JS: 0=Sun..6=Sat -> Mon=0..Sun=6)
      const jsDow = firstDay.getDay();
      const monOffset = (jsDow + 6) % 7;

      const cells = [];

      for (let i = 0; i < monOffset; i++) {
        cells.push({ day: null });
      }

      for (let day = 1; day <= lastDay.getDate(); day++) {
        const d = new Date(this.year, monthIndex, day);
        d.setHours(0, 0, 0, 0);
        const key = this.dateKey(d);
        const realized = this.realizedDayMap[key];
        const planned = this.plannedDayMap[key];

        let mode = "none";
        let title = "";

        if (d <= this.today && realized) {
          mode = realized.statusId === 2 ? "past-go" : "past-other";
          title = `${day}. ${MONTH_NAMES[monthIndex]} \u2014 ${realized.code} (realizovano)`;
        } else if (d > this.today && planned) {
          mode = "future";
          title = `${day}. ${MONTH_NAMES[monthIndex]} \u2014 ${planned.code} (planirano)`;
        }

        cells.push({
          day,
          mode,
          title,
        });
      }

      return {
        index: monthIndex,
        name: MONTH_NAMES[monthIndex],
        cells,
      };
    },

    cellClass(cell) {
      if (!cell.day) return "ev-day-cell--empty";
      if (cell.mode === "past-go") return "ev-day-cell--past-go";
      if (cell.mode === "past-other") return "ev-day-cell--past-other";
      if (cell.mode === "future") return "ev-day-cell--future";
      return "";
    },
  },
};
</script>

<style scoped>
.ev-year-calendar {
  margin-top: 14px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.ev-year-calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.ev-year-calendar-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #2d3748;
}

.ev-year-calendar-legend {
  display: flex;
  gap: 12px;
}

.ev-yc-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #4a5568;
}

.ev-yc-swatch {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
}

.ev-yc-swatch--past-go {
  background: #3182ce;
}

.ev-yc-swatch--past-other {
  background: #a0aec0;
}

.ev-yc-swatch--future {
  background: #b7791f;
}

.ev-year-calendar-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
}

@media (max-width: 1100px) {
  .ev-year-calendar-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .ev-year-calendar-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.ev-month-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px;
}

.ev-month-name {
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ev-month-weekdays {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 1px;
  margin-bottom: 2px;
}

.ev-month-weekdays span {
  text-align: center;
  font-size: 9px;
  font-weight: 600;
  color: #a0aec0;
}

.ev-month-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 1px;
}

.ev-day-cell {
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  color: #4a5568;
  border-radius: 2px;
  background: #ffffff;
}

.ev-day-cell--empty {
  background: transparent;
}

.ev-day-cell--past-go {
  background: #3182ce;
  color: #ffffff;
}

.ev-day-cell--past-other {
  background: #a0aec0;
  color: #ffffff;
}

.ev-day-cell--future {
  background: #b7791f;
  color: #ffffff;
}
</style>
